import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatDate } from "../../../functions/dateTime/formatDate";
import { getmaterialTransportStatus } from "./siteHelpers";
import { checkNotNull } from "../../../functions/checkValue";

export const exportMaterialTransportReport = async (
  transportData = [],
  fileName = "Material_Transport_Report",
) => {
  if (!Array.isArray(transportData) || transportData.length === 0) {
    alert("No data available to export");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Material Transport");

  // Helper functions
  const getMaterialId = (item) => item.material_id ?? item.material?.id ?? null;

  const getMaterialKey = (item) =>
    String(getMaterialId(item) ?? item.material?.name ?? "unknown");

  // Columns matching your screenshots
  const columns = [
    { header: "MATERIAL", key: "material", width: 24 },
    { header: "FIELD TECHNICIAN", key: "technician", width: 27 },
    { header: "SUPERVISOR", key: "supervisor", width: 25 },
    { header: "VEHICLE NUMBER", key: "vehicleNumber", width: 18 },
    { header: "DRIVER NAME", key: "driverName", width: 20 },
    { header: "TRANSPORT DATE", key: "transportDate", width: 18 },
    { header: "REQUIRED QUANTITY", key: "quantityRequired", width: 21 },
    {
      header: "QUANTITY DISPATCHED FROM FACTORY",
      key: "quantityDispatchedFromFactory",
      width: 28,
    },
    {
      header: "QUANTITY RECEIVED AT SITE",
      key: "quantityReceivedAtSite",
      width: 25,
    },
    {
      header: "QUANTITY DISPATCHED FROM SITE",
      key: "quantityDispatchedFromSite",
      width: 27,
    },
    {
      header: "QUANTITY RECEIVED AT FACTORY",
      key: "quantityReceivedAtFactory",
      width: 28,
    },
    {
      header: "EXCESS AT FACTORY",
      key: "excessAtFactory",
      width: 28,
    },
    {
      header: "EXCESS AT SITE",
      key: "excessAtSite",
      width: 28,
    },
    {
      header: "STATUS",
      key: "status",
      width: 20,
    },
  ];

  worksheet.columns = columns;

  // Sort by Material ID, then Transport Date (latest first)
  // const sortedData = [...transportData].sort((a, b) => {
  //   // 1. Group by Material ID
  //   const materialDifference =
  //     Number(getMaterialId(a) ?? Number.MAX_SAFE_INTEGER) -
  //     Number(getMaterialId(b) ?? Number.MAX_SAFE_INTEGER);

  //   if (materialDifference !== 0) return materialDifference;

  //   // 2. Sort each material by date (latest to oldest)
  //   const dateA = String(a.dateOfTransport ?? "");
  //   const dateB = String(b.dateOfTransport ?? "");

  //   const dateDifference = dateA.localeCompare(dateB);

  //   if (dateDifference !== 0) return dateDifference;
  // });

  const sortedData = [...transportData].sort((a, b) => {
  // 1. Sort by Material ID
  const materialDifference =
    Number(getMaterialId(a) ?? Number.MAX_SAFE_INTEGER) -
    Number(getMaterialId(b) ?? Number.MAX_SAFE_INTEGER);

  if (materialDifference !== 0) return materialDifference;

  // 2. Within the same material, sort Field Technician alphabetically
  const technicianA = String(
    a.fieldTechnician?.fullName ?? ""
  ).trim();

  const technicianB = String(
    b.fieldTechnician?.fullName ?? ""
  ).trim();

  return technicianA.localeCompare(technicianB, undefined, {
    sensitivity: "base",
  });
});

  // Add one row per transport record.
  sortedData.forEach((item) => {
    const materialName = item.material?.name?.trim();
    const technicianName = item.fieldTechnician?.fullName?.trim();

    worksheet.addRow({
      material: materialName,
      technician: technicianName,
      supervisor: item.supervisor?.fullName?.trim() ?? "",
      vehicleNumber: item.vehicleNumber ?? "",
      driverName: item.driverName ?? "",
      transportDate: formatDate(item.dateOfTransport),
      quantityRequired: checkNotNull(item.quantityRequired),
      quantityDispatchedFromFactory: checkNotNull(
        item.quantityDispatchedFromFactory,
      ),
      quantityReceivedAtSite: checkNotNull(item.quantityReceivedAtSite),
      quantityDispatchedFromSite: checkNotNull(item.quantityDispatchedFromSite),
      quantityReceivedAtFactory: checkNotNull(item.quantityReceivedAtFactory),
      excessAtFactory: checkNotNull(item.excessAtFactory),
      excessAtSite: checkNotNull(item.excessAtSite),
      status: getmaterialTransportStatus(item.status).status,
    });
  });

  // Merge MATERIAL cells across consecutive rows of the same material.
  // Excel row 1 is the header; data starts at row 2.
  // let groupStartRow = 2;

  // for (let i = 1; i <= sortedData.length; i++) {
  //   const previousKey = getMaterialKey(sortedData[i - 1]);

  //   const currentKey =
  //     i < sortedData.length ? getMaterialKey(sortedData[i]) : null;

  //   if (currentKey !== previousKey) {
  //     const groupEndRow = i + 1;

  //     if (groupEndRow > groupStartRow) {
  //       worksheet.mergeCells(groupStartRow, 1, groupEndRow, 1);
  //     }

  //     worksheet.getCell(groupStartRow, 1).alignment = {
  //       vertical: "middle",
  //       horizontal: "center",
  //       wrapText: true,
  //     };

  //     worksheet.getCell(groupStartRow, 1).font = {
  //       bold: true,
  //       size: 11,
  //     };

  //     groupStartRow = i + 2;
  //   }
  // }

  // Header styling
  const headerRow = worksheet.getRow(1);
  headerRow.height = 42;

  headerRow.eachCell((cell) => {
    cell.font = {
      bold: true,
      size: 10,
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  });

  // Format dates
  worksheet.getColumn("transportDate").numFmt = "dd mmm yyyy";

  // Borders and alignment for the whole sheet
  for (let rowNumber = 1; rowNumber <= worksheet.rowCount; rowNumber++) {
    const row = worksheet.getRow(rowNumber);

    if (rowNumber > 1) {
      row.height = 32;
    }

    for (let colNumber = 1; colNumber <= columns.length; colNumber++) {
      const cell = row.getCell(colNumber);
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    }
  }

  // Keep the header visible while scrolling.
  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
    },
  ];

  // Generate and download file
  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${fileName}.xlsx`,
  );
};
